import React, { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertButton, Button, OutlineButton, TertiaryButton } from '@/components/display/button';
import { ExecutePlanIcon } from '@/icons/execute-plan-icon';
import Drawer from '@/components/feedback/drawer';
import { Modal } from '@/components/feedback/modal';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { useProposalContext } from '@/features/proposals/proposal-context';
import { DBStrategy } from 'shared/src/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsUp, faTriangleExclamation, faXmark } from '@fortawesome/pro-light-svg-icons';
import { equalToTwoDecimalPlaces } from 'shared/src/utils/number/number-utils';
import {
  LineItemChangesApi,
  ProposalPromoteDiffApiResponse,
  StrategyDiffApi
} from 'shared/src/api/strategy-api-types';
import { PendingChangeContainer } from '@/features/proposals/promotion/proposal-change-container';
import {
  changedLineItemFields,
  changedStrategyFields,
  DeletedField,
  DeletedLineItem,
  hasBlockedDeletions,
  hasUnpricedLineItems,
  NewField,
  NewLineItem,
  proposalHasChanges,
  UpdatedField,
  UpdatedLineItem
} from '@/features/proposals/promotion/proposal-diff-utils';
import { SubmitSection } from '@/features/proposals/promotion/proposal-submit-section';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { isLineItemUnpriced } from 'shared/src/domain/line-item/line-item-utils';
import { trpc } from '@/utils/trpc-client';
import clsx from 'clsx';

export function ExecuteProposalButton() {
  const [open, setOpen] = React.useState(false);
  const { diff } = useProposalContext();
  const disabled = !proposalHasChanges(diff);

  const totalChanges =
    diff.strategy.field_changes.length +
    diff.line_items.to_create.length +
    diff.line_items.to_update.length +
    diff.line_items.to_delete.length;

  return (
    <>
      <Button
        size="compact"
        data-tooltip-id="execute-proposal"
        onClick={() => setOpen(true)}
        disabled={disabled}>
        <div className="flex items-center gap-2">
          <ExecutePlanIcon color={'white'} />
          <div>Execute Plan</div>
          {!disabled && (
            <div className="rounded bg-blue-100 px-2 py-0.4 text-sm font-semibold text-blue-700">
              {totalChanges}
            </div>
          )}
        </div>
      </Button>
      <Drawer open={open} setOpen={setOpen}>
        <ProposalDiff setOpen={setOpen} />
      </Drawer>
    </>
  );
}

type ProposalDiffProps = {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

function ProposalDiff({ setOpen }: ProposalDiffProps) {
  const { campaign } = useCampaignContext();
  const { proposal, lineItems, diff } = useProposalContext();
  const { strategy, line_items } = diff;
  const hasChanges = proposalHasChanges(diff);
  return (
    <div className="flex h-full w-[650px] flex-col bg-white py-8 shadow-xl">
      <div className="flex justify-between mb-4 px-8">
        <div className="text-xl font-bold">Ready Check</div>
        <div className="flex items-center gap-3">
          {hasChanges && strategy.exists_in_execution ? (
            <DiscardAllButton proposalId={proposal.id} />
          ) : null}
          <CloseButton onClick={() => setOpen(false)} />
        </div>
      </div>
      <div className="flex items-center text-sm text-gray-500 px-8">
        <div className="mr-2">Campaign:</div>
        <div>{campaign.campaign_name}</div>
      </div>

      {isNotFullAllocated(proposal, lineItems) ? <NotAllocatedError /> : null}
      {hasBlockedDeletions(diff) ? <MediaBuyBlockError diff={diff} /> : null}
      {hasUnpricedLineItems(lineItems) ? <UnpricedLineItemsError lineItems={lineItems} /> : null}
      {!isNotFullAllocated(proposal, lineItems) &&
      !hasBlockedDeletions(diff) &&
      !hasUnpricedLineItems(lineItems) ? (
        <CheckOk />
      ) : null}
      <div className="h-[1px] w-full bg-gray-200 my-2 px-8" />
      <div className="mt-4 flex-1 overflow-y-auto px-8">
        <StrategyChanges changes={strategy} />
        <LineItemChanges changes={line_items} />
      </div>
      <SubmitSection setOpen={setOpen} />
    </div>
  );
}

function DiscardAllButton({ proposalId }: { proposalId: string }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const utils = trpc.useUtils();
  const { mutateAsync, isPending } = trpc.discardAllProposalChanges.useMutation({
    onSuccess: () => {
      utils.getProposal.invalidate();
      utils.getProposalLineItems.invalidate();
      utils.proposalDiff.invalidate();
      utils.campaignV2.invalidate();
    }
  });

  return (
    <>
      <button
        className="cursor-pointer rounded px-2 py-1 text-sm font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
        disabled={isPending}
        onClick={() => setConfirmOpen(true)}>
        Discard All
      </button>
      <Modal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        header={
          <div className="flex flex-col items-center gap-2 rounded-t-2xl bg-red-100 py-6">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-600" size="2x" />
            <h2 className="text-center text-xl font-semibold text-red-700">Discard all changes?</h2>
          </div>
        }
        content={
          <div className="px-10 pt-6 text-base leading-6 text-gray-700">
            <p>This will revert all pending changes back to the current execution state.</p>
          </div>
        }
        footer={
          <div className="flex gap-4 px-10 pb-4 pt-8">
            <TertiaryButton
              className="w-[190px] justify-center"
              onClick={() => setConfirmOpen(false)}>
              Cancel
            </TertiaryButton>
            <AlertButton
              className="flex-1 justify-center"
              disabled={isPending}
              onClick={() => {
                mutateAsync({ strategy_id: proposalId }).then(() => setConfirmOpen(false));
              }}>
              Discard All Changes
            </AlertButton>
          </div>
        }
      />
    </>
  );
}

function StrategyChanges({ changes }: { changes: StrategyDiffApi }) {
  const { campaign } = useCampaignContext();
  const { proposal } = useProposalContext();
  if (changes.field_changes.length === 0) return null;
  const numUpdates = changes.field_changes.length;
  const { type, items } = changedStrategyFields(changes);
  const utils = trpc.useUtils();
  const { mutateAsync } = trpc.discardProposalStrategyChanges.useMutation({
    onSuccess: () => {
      utils.getProposal.invalidate();
      utils.getProposalLineItems.invalidate();
      utils.proposalDiff.invalidate();
    }
  });

  return (
    <div className="mb-8">
      <SectionHeader label="Campaign" />
      <PendingChangeContainer
        name={campaign.campaign_name}
        numChanges={numUpdates}
        discardFn={() => mutateAsync({ strategy_id: proposal.id })}
        content={
          <ChangeContainer>
            {type === 'new' && items.map(f => <NewChangeItem field={f} key={f.label} />)}
            {type === 'update' && items.map(f => <UpdateChangeItem field={f} key={f.label} />)}
          </ChangeContainer>
        }
        tooltipContent={null}
        tooltipId="pending-strategy-update"
        type={type}
      />
    </div>
  );
}

function LineItemChanges({ changes }: { changes: LineItemChangesApi }) {
  const lineItems = changedLineItemFields(changes);

  return (
    <div>
      <SectionHeader label="Line Items" />
      {lineItems.map(lineItem => (
        <LineItemChange key={lineItem.id} lineItem={lineItem} />
      ))}
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="w-full bg-gray-100 rounded-sm px-2 py-1 font-bold text-sm mb-2">{label}</div>
  );
}

type LineItemChangeProps = {
  lineItem: NewLineItem | UpdatedLineItem | DeletedLineItem;
};

function LineItemChange({ lineItem }: LineItemChangeProps) {
  const { type, name, items } = lineItem;
  const isBlocked = type === 'delete' && lineItem.blocked_by_media_buys;
  const { proposal } = useProposalContext();
  const utils = trpc.useUtils();
  const { mutateAsync } = trpc.discardProposalLineItemChanges.useMutation({
    onSuccess: () => {
      utils.getProposalLineItems.invalidate();
      utils.proposalDiff.invalidate();
    }
  });
  return (
    <PendingChangeContainer
      name={name}
      numChanges={items.length}
      discardFn={() => mutateAsync({ strategy_id: proposal.id, line_item_id: lineItem.id })}
      content={
        <ChangeContainer>
          {type === 'new' && items.map(f => <NewChangeItem field={f} key={f.label} />)}
          {type === 'update' && items.map(f => <UpdateChangeItem field={f} key={f.label} />)}
          {type === 'delete' && items.map(f => <DeleteChangeItem field={f} key={f.label} />)}
          {isBlocked ? (
            <div className="mt-1 flex items-center gap-2 rounded-sm bg-red-50 px-4 py-2 text-sm text-red-700">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>Has active media buy assignments. Remove them to enable deletion.</span>
            </div>
          ) : null}
        </ChangeContainer>
      }
      tooltipContent={null}
      tooltipId={`pending-line-item-${lineItem.id}`}
      type={type}
    />
  );
}

function isNotFullAllocated(proposal: DBStrategy, lineItems: LineItemTrpcResponse[]) {
  return !equalToTwoDecimalPlaces(
    proposal.price,
    lineItems.map(l => l.price).reduce((a, b) => a + b, 0)
  );
}

function NotAllocatedError() {
  return (
    <div className="flex items-center rounded-xl bg-red-600 p-0 my-2 mx-8">
      <div className="px-3 py-2 rounded-l-xl h-full flex items-center">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-300" size="lg" />
      </div>
      <div className="p-2 bg-red-200 rounded-xl w-full">
        <div className="font-semibold">Price not fully allocated</div>
        <div className="text-sm">Line item price must sum up to the campaign price</div>
      </div>
    </div>
  );
}

function MediaBuyBlockError({ diff }: { diff: ProposalPromoteDiffApiResponse }) {
  const { campaign } = useCampaignContext();
  const blockedItems = diff.line_items.to_delete.filter(item => item.blocked_by_media_buys);
  return (
    <div className="flex items-center rounded-xl bg-red-600 p-0 my-2 mx-8">
      <div className="px-3 py-2 rounded-l-xl h-full flex items-center self-start">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-300" size="lg" />
      </div>
      <div className="p-2 bg-red-200 rounded-xl w-full">
        <div className="font-semibold">Cannot execute</div>
        <div className="text-sm">
          The following line items have active media buy assignments. Remove them before executing.
        </div>
        <ul className="mt-1 text-sm">
          {blockedItems.map(item => (
            <li key={item.id}>
              <Link
                to={`/campaigns/${campaign.campaign_id}/execution/lineitems/${item.id}`}
                className="font-medium text-red-800 underline">
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function UnpricedLineItemsError({ lineItems }: { lineItems: LineItemTrpcResponse[] }) {
  const { campaign } = useCampaignContext();
  const unpricedItems = lineItems.filter(isLineItemUnpriced);
  return (
    <div className="flex items-center rounded-xl bg-red-600 p-0 my-2 mx-8">
      <div className="px-3 py-2 rounded-l-xl h-full flex items-center self-start">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-300" size="lg" />
      </div>
      <div className="p-2 bg-red-200 rounded-xl w-full">
        <div className="font-semibold">Cannot execute</div>
        <div className="text-sm">
          The following line items are missing a unit price or planned cost. Set values before
          executing.
        </div>
        <ul className="mt-1 text-sm">
          {unpricedItems.map(item => (
            <li key={item.id}>
              <Link
                to={`/campaigns/${campaign.campaign_id}/proposal/lineitems/${item.id}`}
                className="font-medium text-red-800 underline">
                {item.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CheckOk() {
  return (
    <div className="flex items-center rounded-xl bg-lime-600 p-0 my-2 mx-8">
      <div className="px-3 py-2 rounded-l-xl h-full flex items-center">
        <FontAwesomeIcon icon={faThumbsUp} className="text-lime-300" size="lg" />
      </div>
      <div className="px-2 py-4 bg-lime-200 rounded-xl w-full">
        <div className="font-semibold">Check Cleared</div>
      </div>
    </div>
  );
}

type ChangeItemProps = {
  field: NewField;
};

function NewChangeItem({ field }: ChangeItemProps) {
  return (
    <FieldContainer>
      <FieldLabel>{field.label}</FieldLabel>
      <FieldValue type="new">
        <div className="w-1/2 text-gray-600">{field.value}</div>
      </FieldValue>
    </FieldContainer>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <OutlineButton onClick={onClick} className="w-[100px]">
      <div>Close</div>
      <FontAwesomeIcon icon={faXmark} />
    </OutlineButton>
  );
}

type UpdateChangeItemProps = {
  field: UpdatedField;
};

function UpdateChangeItem({ field }: UpdateChangeItemProps) {
  return (
    <FieldContainer>
      <FieldLabel>{field.label}</FieldLabel>
      <FieldValue type="update">
        <div className="w-1/2 text-[#6B7280] line-through">{field.oldValue || 'Empty'}</div>
        <div className="flex w-1/2 items-center">
          <div className="mr-2">→</div>
          <div>{field.newValue || ''}</div>
        </div>
      </FieldValue>
    </FieldContainer>
  );
}

type DeleteChangeItemProps = {
  field: DeletedField;
};

function DeleteChangeItem({ field }: DeleteChangeItemProps) {
  return (
    <FieldContainer>
      <FieldLabel>{field.label}</FieldLabel>
      <FieldValue type="delete">
        <div className="w-1/2 text-[#6B7280] line-through">{field.value || 'Empty'}</div>
      </FieldValue>
    </FieldContainer>
  );
}

function ChangeContainer({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-0.5">{children}</div>;
}

function FieldContainer({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-between pl-4 text-sm">{children}</div>;
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <div className="basis-32 font-medium">{children}</div>;
}

function FieldValue({
  type,
  children
}: {
  type: 'new' | 'update' | 'delete';
  children: ReactNode;
}) {
  return (
    <div
      className={clsx(
        'flex grow items-center rounded-sm px-2 py-1.5 font-light',
        type === 'update' ? 'bg-orange-100' : type === 'delete' ? 'bg-red-100' : 'bg-blue-100'
      )}>
      {children}
    </div>
  );
}
